import * as S from './styled'

import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from 'i18next'

import api from '@/core/services/api'
import i18n from '@/constant/locales/i18n'
import { getBaseCurrency } from '@/constant/currency'
import { setMenuData } from '@/core/store/menu/actions'
import { useCommonState } from '@/core/context/useContext'
import { usePopup } from '@/core/context/useWindow'
import Login from '@/components/popup/Login'
import GameThumbnail from '@/components/GameThumbnail'
import { getLobbyLogo } from '@/components/gold/vendorLogos'
import iconsSprite from '@/assets/icons.svg'

const IMG_BASE = process.env.REACT_APP_IMG_URL || ''

/**
 * 게임사 게임 목록 페이지(PC) — gold8.bet /GenericCasino 모양.
 *   메인의 게임사 타일 「スロット」·「ライブ」를 누르면 새 창(탭)으로 열린다.
 *   /lobby/:menuCode?category=slot|live
 *   구성: 게임사 로고 + 노란 검색 상자 → 알약 탭(新着/人気/全てのゲーム) → 게임사 배너 → 섹션(노란 머리표) + 흰 게임 카드.
 *   人気 탭(처음 화면)은 gold 처럼 新着 가로 줄 + 人気 격자를 같이 보여준다.
 *   게임 데이터는 예전 게임 목록 팝업과 같은 API(/game/side-menu/game).
 */
const TABS = [
  { key: 'new', label: () => t('gameList.new') },
  { key: 'popular', label: () => t('gameList.popular') },
  { key: 'all', label: () => t('gameList.allGames') },
]

const fetchGames = (menuCode, kind) => {
  const params = { menu_code: menuCode, lang: i18n.language }
  if (kind === 'new') params.is_new = 'Y'
  else if (kind === 'popular') params.is_popular = 'Y'
  return api.get('/game/side-menu/game', { params }).then((res) => {
    const content = res.data?.content
    return content?.game_list?.content || content?.game_list || []
  })
}

export default function Lobby() {
  const { menuCode } = useParams()
  const [searchParams] = useSearchParams()
  const category = searchParams.get('category') === 'live' ? 'live' : 'slot'
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isLogin } = useCommonState()
  const { openPopup } = usePopup()
  const menuData = useSelector((state) => state.Menu)

  // 새 창이라 메뉴(게임사 목록) 데이터가 비어 있으면 직접 받아온다
  useEffect(() => {
    if (menuData?.length) return
    api.get('/game/side-menu')
      .then((res) => dispatch(setMenuData(res?.data?.content?.side_menu || [])))
      .catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const vendor = useMemo(() => {
    const findNode = (code) =>
      menuData?.find?.(m => m.menuCode === code) ||
      menuData?.[0]?.subMenus?.find?.(m => m.menuCode === code)
    const all = [...(findNode('01_02')?.subMenus || []), ...(findNode('01_01')?.subMenus || [])]
    return all.find(v => v.menuCode === menuCode)
  }, [menuData, menuCode])

  useEffect(() => {
    if (vendor?.name) document.title = vendor.name
  }, [vendor?.name])

  const [tab, setTab] = useState('popular')
  const [keyword, setKeyword] = useState('')
  const [lists, setLists] = useState({ new: [], popular: [], all: [] })
  const [loading, setLoading] = useState(true)

  // 탭마다 필요한 목록만 받는다 — 人気 탭은 新着 + 人気 두 개
  useEffect(() => {
    if (!menuCode) return
    const kinds = tab === 'popular' ? ['new', 'popular'] : [tab]
    setLoading(true)
    Promise.all(kinds.map(k => fetchGames(menuCode, k).then(list => [k, list])))
      .then(pairs => setLists(prev => ({ ...prev, ...Object.fromEntries(pairs) })))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [menuCode, tab])

  const match = (g) => !keyword || g.title?.toLowerCase().includes(keyword.toLowerCase())

  const playGame = (game) => {
    if (!isLogin) {
      openPopup({ grandLogin: true, title: t('modalTitle.login'), content: <Login /> })
      return
    }
    // 게임 화면의 「戻る」가 이 목록으로 돌아오도록 게임사·카테고리를 넘긴다
    navigate(`/game-play/${game.vendor}/${game.id}/${getBaseCurrency()}`, {
      state: { vendorIndex: menuCode, category },
    })
  }

  const renderCard = (g, i) => (
    <S.Card key={g.game_id || g.id || i} onClick={() => playGame(g)} title={g.title}>
      <S.ImgBox>
        <GameThumbnail src={g.thumbnail || ''} alt={g.title || ''} />
        <S.Mask aria-hidden="true"><S.Play>▶</S.Play></S.Mask>
      </S.ImgBox>
      <S.Name>
        <span>{g.title}</span>
        <S.Heart aria-hidden="true">♡</S.Heart>
      </S.Name>
    </S.Card>
  )

  const renderSection = (title, items, slider = false) => (
    <S.Section>
      <S.SectionTitle><span>{title}</span></S.SectionTitle>
      {loading && !items.length ? (
        <S.Grid>{Array.from({ length: 14 }).map((_, i) => <S.CardSkeleton key={i} />)}</S.Grid>
      ) : slider ? (
        <S.Slider>{items.filter(match).map(renderCard)}</S.Slider>
      ) : (
        <S.Grid>{items.filter(match).map(renderCard)}</S.Grid>
      )}
    </S.Section>
  )

  const logo = getLobbyLogo(vendor?.name)

  return (
    <S.Page>
      <S.Header>
        <S.Wrapper $row>
          <S.Logo>
            {logo ? <img src={logo} alt={vendor?.name || ''} /> : <S.LogoText>{vendor?.name || ''}</S.LogoText>}
          </S.Logo>
          <S.Search onSubmit={(e) => e.preventDefault()}>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t('gameList.searchPlaceholder')}
            />
            <button type="submit" aria-label="search">
              <svg viewBox="0 0 47 47" aria-hidden="true"><use href={`${iconsSprite}#ico-sch`} /></svg>
            </button>
          </S.Search>
        </S.Wrapper>
      </S.Header>

      <S.Nav>
        <S.Wrapper>
          <S.Tabs>
            {TABS.map(({ key, label }) => (
              <li key={key}>
                <S.TabBtn type="button" $active={tab === key} onClick={() => setTab(key)}>{label()}</S.TabBtn>
              </li>
            ))}
          </S.Tabs>
        </S.Wrapper>
      </S.Nav>

      <S.Wrapper $content>
        {vendor?.vendorThumbnail && (
          <S.Banner src={`${IMG_BASE}${vendor.vendorThumbnail}`} alt="" />
        )}
        {tab === 'popular' && (
          <>
            {renderSection(t('gameList.new'), lists.new, true)}
            {renderSection(t('gameList.popular'), lists.popular)}
          </>
        )}
        {tab === 'new' && renderSection(t('gameList.new'), lists.new)}
        {tab === 'all' && renderSection(t('gameList.allGames'), lists.all)}
      </S.Wrapper>
    </S.Page>
  )
}
